import { isEmpty } from 'lodash';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { Button } from '@progress/kendo-react-buttons';
import { useContext, useEffect, useState } from 'react';

import {
  LockAsync,
  DisableTeamChatAsync,
  sendFacilitatorTeamChat,
  giveItem as teamGiveItem,
  showArea as teamShowArea,
  openTask as teamOpenTask,
  showTask as teamShowTask,
  giveScore as teamGiveScore,
  giveTitle as teamGiveTitle,
  pauseTimer as teamPauseTimer,
  startTimer as teamStartTimer,
  navigateToZone as teamNavigateToZone,
  navigateToWorldMap as teamNavigateToWorldMap
} from '../../services/teams';
import {
  giveItem as groupGiveItem,
  showArea as groupShowArea,
  openTask as groupOpenTask,
  showTask as groupShowTask,
  giveScore as groupGiveScore,
  giveTitle as groupGiveTitle,
  pauseTimer as groupPauseTimer,
  startTimer as groupStartTimer,
  navigateToZone as groupNavigateToZone,
  navigateToWorldMap as groupNavigateToWorldMap
} from '../../services/groups';
import {
  KickPlayerAsync,
  ChatFromFacilitator,
  PostChatFromFacilitator,
  giveItem as playerGiveItem,
  showArea as playerShowArea,
  showTask as playerShowTask,
  openTask as playerOpenTask,
  giveScore as playerGiveScore,
  giveTitle as playerGiveTitle,
  pauseTimer as playerPauseTimer,
  startTimer as playerStartTimer,
  navigateToZone as playerNavigateToZone,
  navigateToWorldMap as playerNavigateToWorldMap
} from '../../services/players';
import Popup from '../popup-window';
import {
  NotificationContent,
  NotificationContext
} from '../../contexts/notification';
import { GameAlgorithm } from '../game-algorithm';
import ComboboxInput from '../forms/combobox-input';
import { useBankAction } from '../../hooks/use-inventory-bank';
import { TeamResponse } from '../../types/responses/team-response';
import { PlayerStateFull, TeamStateFull } from '../../types/state';
import { postBroadcastGameStateAsync } from '../../services/games';
import { GroupResponse } from '../../types/responses/group-response';
import { PlayerResponse } from '../../types/responses/player-response';
import { FacilitatorTeamChat } from '../../pages/facilitator/chat/team-chat';
import { TeamStateDashboard } from './state-visualisation/team-state-dashboard';
import { PlayerStateDashboard } from './state-visualisation/player-state-dashboard';
import { FacilitatorAndPlayerChat } from '../../pages/facilitator/chat/facilitator-player-chat';

interface ListProps {
  state?: any;
  isPlayer: boolean;
  isGroup?: boolean;
  teamCode?: string;
  groupCode?: string;
  nameState?: string;
  isLocked?: boolean;
  isKicked?: boolean;
  player?: PlayerResponse;
  isDisabledChat?: boolean;
  teamList?: TeamResponse[];
  isDisableConfirm?: boolean;
  selectedTeam?: TeamResponse;
  groupList?: GroupResponse[];
  selectedGroup?: GroupResponse;
  playerOrTeamCode: string | number;
  handleRemoveGroup?: () => void;
  handleAddTeamToGroup?: () => void;
  handleAddPlayerToTeam?: () => void;
  handleRemoveTeamFromGroup?: () => void;
  handleRemovePlayerFromTeam?: () => void;
  handleChangeTeam?: (team: TeamResponse) => void;
  handleChangeGroup?: (group: GroupResponse) => void;
  onDisabledChatTeam?: (teamcode: string, isDisabledChat: boolean) => void;
}

interface AlgorithmState {
  quantity?: number;
  operation?: string;
  argument?: string | string[];
}

export const FacilitatorState = ({
  state,
  player,
  teamCode,
  isLocked,
  isKicked,
  groupCode,
  nameState,
  teamList = [],
  groupList = [],
  isDisabledChat,
  isPlayer = true,
  isGroup = false,
  playerOrTeamCode,
  selectedTeam = {},
  selectedGroup = {},
  isDisableConfirm = false,
  onDisabledChatTeam,
  handleChangeTeam = () => {},
  handleChangeGroup = () => {},
  handleRemoveGroup = () => {},
  handleAddTeamToGroup = () => {},
  handleAddPlayerToTeam = () => {},
  handleRemoveTeamFromGroup = () => {},
  handleRemovePlayerFromTeam = () => {}
}: ListProps) => {
  const { gameCode } = useParams();

  const auth = useAuth();
  const userId = parseInt(auth.user?.profile.sub!);
  const userName = auth.user?.profile.name!;

  const [algorithmState, setAlgorithmState] = useState<AlgorithmState>({});
  const [isLockTeam, setIsLockTeam] = useState<boolean>(isLocked!);
  const [isDisabledChatTeam, setIsDisabledChatTeam] = useState<boolean>(
    isDisabledChat ? isDisabledChat : false
  );
  const [isPlayerKicked, setIsPlayerKicked] = useState<boolean>(isKicked!);
  const [notification, setNotification] = useContext(NotificationContext);
  const [showChat, setShowChat] = useState<boolean>(false);
  const [showChatTeam, setShowChatTeam] = useState<boolean>(false);

  const { trigger } = useBankAction(gameCode ?? '');

  const onChangeOperationHandler = (operation?: string) => {
    setAlgorithmState({ ...algorithmState, operation: operation });
  };

  const onChangeArgumentHandler = (selectedArguments?: string | string[]) => {
    setAlgorithmState({ ...algorithmState, argument: selectedArguments });
  };

  const onChangeQuantityHandler = (quantity?: number) => {
    setAlgorithmState({ ...algorithmState, quantity: quantity });
  };

  const responseTeamNotification = (
    message: string,
    isError: boolean = false
  ) => {
    const responseTeamNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>{message}</strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(responseTeamNotification);
    setNotification({ ...notification, content });
  };

  const onConfirmHandler = async () => {
    try {
      if (
        algorithmState &&
        algorithmState.operation?.toLocaleLowerCase().includes('item')
      ) {
        const bankResult = await trigger({
          action:
            algorithmState.operation === 'giveItem' ? 'acquire' : 'return',
          actor: player?.name ?? '',
          actorId: player?.code ?? '',
          itemId: algorithmState.argument as string,
          quantity: algorithmState.quantity ?? 0
        });

        if (!bankResult.success) {
          responseTeamNotification(bankResult.reason ?? '');
          return;
        }
      }

      if (isPlayer) {
        await confirmPlayerHandler();
      } else if (!isPlayer && !isGroup) {
        await confirmTeamHandler();
      } else {
        await confirmGroupHandler();
      }

      responseTeamNotification('Successfully sent the command');
    } catch (error) {
      responseTeamNotification('Failed sent the command', true);
    }
  };

  const confirmPlayerHandler = async () => {
    const code = playerOrTeamCode as string;

    switch (algorithmState?.operation) {
      case 'navigateToWorldMap':
        return await playerNavigateToWorldMap(gameCode!, code!);
      case 'navigateToZone':
        return await playerNavigateToZone(
          gameCode!,
          code!,
          algorithmState?.argument! as string
        );
      case 'openTask':
        return await playerOpenTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string
        );
      case 'showTask':
        return await playerShowTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'hideTask':
        return await playerShowTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'showArea':
        return await playerShowArea(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'hideArea':
        return await playerShowArea(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'startTimer':
        return await playerStartTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'stopTimer':
        return await playerStartTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'pauseTimer':
        return await playerPauseTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'resumeTimer':
        return await playerPauseTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'giveItem':
        return await playerGiveItem(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          algorithmState?.quantity!,
          true
        );
      case 'removeItem':
        return await playerGiveItem(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          algorithmState?.quantity! * -1,
          false
        );
      case 'giveScore':
        return await playerGiveScore(
          gameCode!,
          code!,
          algorithmState?.quantity!,
          true
        );
      case 'removeScore':
        return await playerGiveScore(
          gameCode!,
          code!,
          algorithmState?.quantity! * -1,
          false
        );
      case 'giveTitle':
        return await playerGiveTitle(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'removeTitle':
        return await playerGiveTitle(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      default:
        break;
    }
  };

  const confirmTeamHandler = async () => {
    const code = playerOrTeamCode as string;

    switch (algorithmState?.operation) {
      case 'navigateToWorldMap':
        return await teamNavigateToWorldMap(gameCode!, code!);
      case 'navigateToZone':
        return await teamNavigateToZone(
          gameCode!,
          code!,
          algorithmState?.argument! as string
        );
      case 'openTask':
        return await teamOpenTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string
        );
      case 'showTask':
        return await teamShowTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'hideTask':
        return await teamShowTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'showArea':
        return await teamShowArea(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'hideArea':
        return await teamShowArea(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'startTimer':
        return await teamStartTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'stopTimer':
        return await teamStartTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'pauseTimer':
        return await teamPauseTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'resumeTimer':
        return await teamPauseTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'giveItem':
        return await teamGiveItem(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          algorithmState?.quantity!,
          true
        );
      case 'removeItem':
        return await teamGiveItem(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          algorithmState?.quantity! * -1,
          false
        );
      case 'giveScore':
        return await teamGiveScore(
          gameCode!,
          code!,
          algorithmState?.quantity!,
          true
        );
      case 'removeScore':
        return await teamGiveScore(
          gameCode!,
          code!,
          algorithmState?.quantity! * -1,
          false
        );
      case 'giveTitle':
        return await teamGiveTitle(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'removeTitle':
        return await teamGiveTitle(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      default:
        break;
    }
  };

  const confirmGroupHandler = async () => {
    const code = groupCode as string;

    switch (algorithmState?.operation) {
      case 'navigateToWorldMap':
        return await groupNavigateToWorldMap(gameCode!, code!);
      case 'navigateToZone':
        return await groupNavigateToZone(
          gameCode!,
          code!,
          algorithmState?.argument! as string
        );
      case 'openTask':
        return await groupOpenTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string
        );
      case 'showTask':
        return await groupShowTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'hideTask':
        return await groupShowTask(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'showArea':
        return await groupShowArea(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'hideArea':
        return await groupShowArea(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'startTimer':
        return await groupStartTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'stopTimer':
        return await groupStartTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'pauseTimer':
        return await groupPauseTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'resumeTimer':
        return await groupPauseTimer(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      case 'giveItem':
        return await groupGiveItem(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          algorithmState?.quantity!,
          true
        );
      case 'removeItem':
        return await groupGiveItem(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          algorithmState?.quantity! * -1,
          false
        );
      case 'giveScore':
        return await groupGiveScore(
          gameCode!,
          code!,
          algorithmState?.quantity!,
          true
        );
      case 'removeScore':
        return await groupGiveScore(
          gameCode!,
          code!,
          algorithmState?.quantity! * -1,
          false
        );
      case 'giveTitle':
        return await groupGiveTitle(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          true
        );
      case 'removeTitle':
        return await groupGiveTitle(
          gameCode!,
          code!,
          algorithmState?.argument! as string,
          false
        );
      default:
        break;
    }
  };

  const LockTeamAsync = async () => {
    try {
      await LockAsync(gameCode!, playerOrTeamCode as string, !isLockTeam);
      await postBroadcastGameStateAsync(gameCode);
    } catch (error) {
      console.error(error);
    }
  };

  const DisableChatAsync = async (isDisabledChatTeam: boolean) => {
    try {
      await DisableTeamChatAsync(
        gameCode!,
        playerOrTeamCode as string,
        isDisabledChatTeam
      );
    } catch (error) {
      console.error(error);
    }
  };

  const ChatHandler = () => {
    let tempIDisabledChatTeam = isDisabledChatTeam;
    try {
      DisableChatAsync(!isDisabledChatTeam);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDisabledChatTeam(!isDisabledChatTeam);
      if (onDisabledChatTeam) {
        onDisabledChatTeam(playerOrTeamCode as string, !tempIDisabledChatTeam);
      }
    }
  };

  // this handler is for the player kicked out of the game
  // temporarily not used due to task 6422, maybe in the future it will be used again
  const KickHandler = () => {
    try {
      KickPlayerAsync(gameCode ?? '', playerOrTeamCode as string);
    } catch (error) {
      console.error(error);
    } finally {
      setIsPlayerKicked(!isPlayerKicked);
    }
  };

  const onSendMessageHandler = async (message: string) => {
    let payload: ChatFromFacilitator = {
      facilitatorName: userName,
      facilitatorUserProfileId: userId,
      toPlayerCode: (player as PlayerResponse).code!,
      message: message
    };

    await PostChatFromFacilitator(gameCode!, payload);
  };

  const onSendMessageTeamHandler = async (message: string) => {
    await sendFacilitatorTeamChat(
      gameCode!,
      selectedTeam?.teamCode,
      userId!,
      userName!,
      message!
    );
  };

  useEffect(() => {
    setIsLockTeam(isLocked!);
    setIsDisabledChatTeam(isDisabledChat!);
  }, [isLocked, nameState, isDisabledChat]);

  return (
    <Row className={'d-flex flex-column gap-4'}>
      {/*Group don't need this*/}
      {!isGroup && (
        <Col className={'col-12'}>
          <div className={'text-break mt-4 fs-6 mt-5'}>
            <div>
              {state ? (
                isPlayer ? (
                  <PlayerStateDashboard
                    state={state as PlayerStateFull}
                    group={groupList?.find(
                      (x) =>
                        x.code ===
                        teamList?.find((x) => x.teamCode === teamCode)
                          ?.groupCode
                    )}
                    team={teamList?.find((x) => x.teamCode === teamCode)}
                  />
                ) : state && !isGroup ? (
                  <TeamStateDashboard
                    state={state as TeamStateFull}
                    group={groupList?.find(
                      (x) => x.code === selectedGroup.code
                    )}
                    selectedTeamCode={selectedTeam.teamCode ?? ''}
                  />
                ) : !isGroup ? (
                  <pre>{`Creating ${
                    isPlayer ? 'Player' : 'Team'
                  } State, Please wait ... `}</pre>
                ) : (
                  ''
                )
              ) : (
                <pre>{`Creating ${
                  isPlayer ? 'Player' : 'Team'
                } State, Please wait ... `}</pre>
              )}
            </div>
          </div>
        </Col>
      )}

      {/*Group don't need this*/}
      {!isGroup && (
        <Col className={'col-12'}>
          <div>
            <hr />
            {state && (
              <div className={'mb-2'}>
                {!isGroup ? (
                  <label className={'text-secondary'}>
                    {isPlayer ? 'Add player to a team' : ''}
                  </label>
                ) : (
                  <b>Group state</b>
                )}
              </div>
            )}
            {/* teams component */}
            {!isGroup && state && !isPlayer && (
              <>
                <div className={'d-flex align-items-center gap-2'}>
                  <ComboboxInput
                    data={groupList}
                    placeholder={'Select a group'}
                    textField={'name'}
                    dataItemKey={'name'}
                    className={'border w-20'}
                    onChange={(e) => {
                      handleChangeGroup(e.target.value);
                    }}
                    value={
                      groupCode !== ''
                        ? groupList?.find((x) => x.code === groupCode)
                        : null
                    }
                  />
                  <Button
                    onClick={handleAddTeamToGroup}
                    fillMode={'outline'}
                    themeColor={'primary'}
                    disabled={isEmpty(groupCode)}>
                    Add to group
                  </Button>
                  <Button
                    themeColor={'primary'}
                    fillMode={'outline'}
                    onClick={handleRemoveTeamFromGroup}
                    disabled={
                      !isEmpty(groupCode) || !isEmpty(selectedGroup.code)
                        ? false
                        : true
                    }>
                    Remove from group
                  </Button>
                </div>
              </>
            )}
            {/* players component */}
            {playerOrTeamCode && state && isPlayer && (
              <>
                <div className={'d-flex align-items-center gap-2'}>
                  <ComboboxInput
                    data={teamList}
                    textField={'name'}
                    dataItemKey={'name'}
                    className={'border border-dark w-20'}
                    onChange={(e) => {
                      handleChangeTeam(e.target.value);
                    }}
                    value={
                      teamCode !== ''
                        ? teamList?.find((x) => x.teamCode === teamCode)
                        : null
                    }
                  />
                  <Button
                    fillMode={'outline'}
                    themeColor={'primary'}
                    onClick={handleAddPlayerToTeam}
                    disabled={!isEmpty(teamCode) ? false : true}>
                    Add to team
                  </Button>
                  <Button
                    fillMode={'outline'}
                    themeColor={'primary'}
                    onClick={handleRemovePlayerFromTeam}
                    disabled={!isEmpty(teamCode) ? false : true}>
                    Remove from team
                  </Button>
                </div>
                {!isPlayer && (
                  <Button
                    themeColor={`${isLockTeam ? 'info' : 'warning'}`}
                    className={'w-10 ml-0 h-3'}
                    onClick={LockTeamAsync}>
                    {isLockTeam ? 'Unlock Team' : 'Lock Team'}
                  </Button>
                )}
              </>
            )}
          </div>
        </Col>
      )}

      {/* players, teams and groups component */}
      {(state || isGroup) && (
        <Col md={{ span: 12 }} className={'pl-1'}>
          <hr className={'ml-2'} />
          <div>
            <label className={'pl-2 text-secondary'}>Manual Action</label>
            <div
              className={
                'd-flex justify-content-between align-items-center w-100'
              }>
              <div>
                <GameAlgorithm
                  onChangeOperation={onChangeOperationHandler}
                  onChangeArgument={onChangeArgumentHandler}
                  onChangeQuantity={onChangeQuantityHandler}
                  disabled={isDisableConfirm}
                />
              </div>
              <Button
                themeColor={'success'}
                className={'w-8 ml-2'}
                disabled={isDisableConfirm}
                onClick={onConfirmHandler}>
                Confirm
              </Button>
            </div>
          </div>
        </Col>
      )}

      {/* teams component */}
      {!isGroup && state && !isPlayer && (
        <Col className={'col-12'}>
          <hr />
          <div className={'d-flex gap-2'}>
            <Button
              onClick={ChatHandler}
              themeColor={'primary'}
              fillMode={'outline'}>
              {isDisabledChatTeam ? 'Enable chat' : 'Disable chat'}
            </Button>
            <Button
              themeColor={'primary'}
              onClick={() => setShowChatTeam(true)}>
              Chat to team
            </Button>
          </div>
          <hr />
          <div>
            <Button
              onClick={LockTeamAsync}
              themeColor={'error'}
              fillMode={'outline'}>
              <span className={'d-flex align-items-center gap-1'}>
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: '20px'
                  }}>
                  lock
                </span>
                {isLockTeam ? 'Unlock team' : 'Lock team'}
              </span>
            </Button>
          </div>
        </Col>
      )}

      {/* players component */}
      {state && isPlayer && (
        <Col className={'col-12'}>
          <hr />
          <div className={'d-flex flex-row-reverse gap-2'}>
            {/* #6422 currently lock team and disable chat just for teams feature */}
            <Button themeColor={'primary'} onClick={() => setShowChat(true)}>
              Chat
            </Button>
          </div>
        </Col>
      )}

      {/* player component */}
      {showChat && player && (
        <Popup
          className={'box d-flex flex-column gap-3 w-50'}
          handleClose={() => setShowChat(false)}>
          <FacilitatorAndPlayerChat
            item={{
              id: player.code ?? '',
              name: player.name ?? '',
              type: 'player-chat'
            }}
            onSendMessage={onSendMessageHandler}
          />
        </Popup>
      )}

      {/* team chat component */}
      {showChatTeam && (
        <Popup
          className={'box d-flex flex-column gap-3 w-50'}
          handleClose={() => setShowChatTeam(false)}>
          <FacilitatorTeamChat
            item={{
              id: selectedTeam.teamCode ?? '',
              name: selectedTeam.name ?? '',
              type: 'team-chat'
            }}
            onSendMessage={onSendMessageTeamHandler}
          />
        </Popup>
      )}
    </Row>
  );
};
